/**
 * Collapse organic/standard SKUs that share variant_family into one picker row.
 * Concrete ingredient IDs still flow to blend eval / cart.
 */

const QUALITY_ORDER = { standard: 0, organic: 1 };

function qualityRank(quality) {
  return QUALITY_ORDER[quality] ?? 99;
}

/**
 * @param {Array<object>} ingredients flat API ingredient list
 * @returns {Array<object>} family rows for the picker
 */
export function collapseIngredientFamilies(ingredients) {
  const byFamily = new Map();
  const order = [];

  for (const ing of ingredients) {
    const key = ing.variant_family || ing.id;
    if (!byFamily.has(key)) {
      byFamily.set(key, []);
      order.push(key);
    }
    byFamily.get(key).push(ing);
  }

  return order.map((familyKey) => {
    const variants = byFamily
      .get(familyKey)
      .slice()
      .sort((a, b) => {
        const q = qualityRank(a.quality) - qualityRank(b.quality);
        if (q !== 0) return q;
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      });

    const primary =
      variants.find((v) => v.quality === 'standard') || variants[0];

    return {
      familyKey,
      name: primary.name,
      type: primary.type,
      botanical_name: primary.botanical_name,
      benefits: primary.benefits,
      caffeine: primary.caffeine,
      capacity_tier: primary.capacity_tier,
      variants,
      hasQualityChoice: variants.length > 1,
    };
  });
}

/**
 * Active concrete SKU for a family row: selected sibling, else preferred in-stock.
 */
export function resolveActiveVariant(family, selectedIngredients, preferredQuality = 'standard') {
  const selected = family.variants.find((v) => selectedIngredients.includes(v.id));
  if (selected) return selected;

  const preferred = family.variants.find(
    (v) => v.quality === preferredQuality && v.in_stock
  );
  if (preferred) return preferred;

  const inStock = family.variants.find((v) => v.in_stock);
  return inStock || family.variants[0];
}

export function findSelectedSibling(family, selectedIngredients) {
  return family.variants.find((v) => selectedIngredients.includes(v.id)) || null;
}
